import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { calendarLocalDateToDate } from "../../lib/calendarLocalDate";
import {
  DAYS_PER_WEEK,
  WEEKDAYS,
  addDays,
  addMonths,
  addMonthsPreservingDay,
  findClosestEnabledDate,
  formatDateKey,
  formatKoreanDateLabel,
  formatMonthName,
  getCalendarWeeks,
  getMonthIndex,
  getSelectionAnnouncement,
  startOfDay,
  startOfMonth,
} from "./datePickerModel";
import { Dialog } from "../Dialog/Dialog";
import styles from "./DatePicker.module.css";

export interface DatePickerProps {
  checkIn: Date | null;
  checkOut: Date | null;
  onDateSelect: (checkIn: Date | null, checkOut: Date | null) => void;
  onClose: () => void;
  onEscape?: () => void;
  datePickerRef?: React.RefObject<HTMLDivElement | null>;
  unavailableDates?: readonly string[];
  disabledRanges?: readonly DatePickerDisabledRange[];
  selectionWindow?: DatePickerSelectionWindow;
  hideFooter?: boolean;
  variant?:
    | "default"
    | "compact"
    | "search"
    | "inline"
    | "sheet"
    | "booking"
    | "review";
  numberOfMonths?: 1 | 2;
  selectionEndpoint?: "checkIn" | "checkOut";
}

interface DatePickerDisabledRange {
  readonly startInclusive: string;
  readonly endExclusive: string;
}

interface DatePickerSelectionWindow {
  readonly startInclusive: string;
  readonly endExclusive: string;
}

const EMPTY_UNAVAILABLE_DATES: readonly string[] = [];
const EMPTY_DISABLED_RANGES: readonly DatePickerDisabledRange[] = [];

export const DatePicker: React.FC<DatePickerProps> = ({
  checkIn,
  checkOut,
  onDateSelect,
  onClose,
  onEscape,
  datePickerRef,
  unavailableDates = EMPTY_UNAVAILABLE_DATES,
  disabledRanges = EMPTY_DISABLED_RANGES,
  selectionWindow,
  hideFooter = false,
  variant = "default",
  numberOfMonths = 1,
  selectionEndpoint,
}) => {
  const [isKeyboardHelpOpen, setIsKeyboardHelpOpen] = useState(false);
  const isBooking = variant === "booking";
  const hasPairedMonthNavigation = isBooking || variant === "review";
  const isSingleMonth = variant === "inline" && numberOfMonths === 1;
  const today = useMemo(() => startOfDay(new Date()), []);
  const todayKey = formatDateKey(today);
  const canonicalSelectionWindow = useMemo(() => {
    if (!selectionWindow) return null;

    const start = calendarLocalDateToDate(selectionWindow.startInclusive);
    const end = calendarLocalDateToDate(selectionWindow.endExclusive);
    if (
      !start ||
      !end ||
      selectionWindow.startInclusive >= selectionWindow.endExclusive
    ) {
      return null;
    }

    return { ...selectionWindow, startDate: start };
  }, [selectionWindow]);
  const minimumDate = canonicalSelectionWindow?.startDate ?? today;
  const minimumDateKey = canonicalSelectionWindow?.startInclusive ?? todayKey;
  const windowEndExclusiveKey = canonicalSelectionWindow?.endExclusive ?? null;
  const checkInKey = checkIn ? formatDateKey(checkIn) : null;
  const checkOutKey = checkOut ? formatDateKey(checkOut) : null;
  const unavailableDateKeys = useMemo(
    () => new Set(unavailableDates),
    [unavailableDates],
  );
  const isBlockedStayDateKey = useCallback(
    (dateKey: string): boolean =>
      unavailableDateKeys.has(dateKey) ||
      disabledRanges.some(
        (range) =>
          dateKey >= range.startInclusive && dateKey < range.endExclusive,
      ),
    [disabledRanges, unavailableDateKeys],
  );
  const isPartialCheckInInvalidForInteraction = Boolean(
    checkInKey !== null &&
    checkOutKey === null &&
    (checkInKey < minimumDateKey ||
      (windowEndExclusiveKey !== null && checkInKey >= windowEndExclusiveKey) ||
      isBlockedStayDateKey(checkInKey)),
  );
  const interactionCheckIn = isPartialCheckInInvalidForInteraction
    ? null
    : checkIn;
  const interactionCheckInKey = isPartialCheckInInvalidForInteraction
    ? null
    : checkInKey;
  const isSelectingCheckOut =
    selectionEndpoint === "checkOut"
      ? interactionCheckIn !== null
      : selectionEndpoint === "checkIn"
        ? false
        : interactionCheckIn !== null && checkOut === null;
  const firstUnavailableDateKeyAfterCheckIn = useMemo(() => {
    if (!isSelectingCheckOut || !interactionCheckInKey) return null;

    const starts = [
      ...unavailableDates.filter((dateKey) => dateKey > interactionCheckInKey),
      ...disabledRanges
        .filter((range) => range.endExclusive > interactionCheckInKey)
        .map((range) =>
          range.startInclusive > interactionCheckInKey
            ? range.startInclusive
            : interactionCheckInKey,
        ),
    ].sort();

    return starts[0] ?? null;
  }, [
    disabledRanges,
    interactionCheckInKey,
    isSelectingCheckOut,
    unavailableDates,
  ]);
  const isDateDisabled = useCallback(
    (date: Date): boolean => {
      const dateKey = formatDateKey(date);

      if (dateKey < minimumDateKey) {
        return true;
      }

      if (isSelectingCheckOut && interactionCheckInKey) {
        return (
          dateKey <= interactionCheckInKey ||
          (windowEndExclusiveKey !== null && dateKey > windowEndExclusiveKey) ||
          (firstUnavailableDateKeyAfterCheckIn !== null &&
            dateKey > firstUnavailableDateKeyAfterCheckIn)
        );
      }

      return (
        (windowEndExclusiveKey !== null && dateKey >= windowEndExclusiveKey) ||
        isBlockedStayDateKey(dateKey)
      );
    },
    [
      firstUnavailableDateKeyAfterCheckIn,
      interactionCheckInKey,
      isSelectingCheckOut,
      isBlockedStayDateKey,
      minimumDateKey,
      windowEndExclusiveKey,
    ],
  );
  const initialFocusedDate = useMemo(
    () =>
      findClosestEnabledDate(checkIn ?? minimumDate, 1, isDateDisabled) ??
      minimumDate,
    [checkIn, isDateDisabled, minimumDate],
  );
  const [currentMonth, setCurrentMonth] = useState<Date>(() =>
    startOfMonth(initialFocusedDate),
  );
  const [focusedDate, setFocusedDate] = useState<Date>(initialFocusedDate);
  const [hoverDate, setHoverDate] = useState<Date | null>(null);
  const focusedDateKey = formatDateKey(focusedDate);
  const hoverDateKey = hoverDate ? formatDateKey(hoverDate) : null;
  const visibleRangeEndKey =
    checkOutKey ??
    (isSelectingCheckOut && hoverDate && !isDateDisabled(hoverDate)
      ? hoverDateKey
      : null);
  const hasVisibleRange = Boolean(
    interactionCheckInKey &&
    visibleRangeEndKey &&
    interactionCheckInKey < visibleRangeEndKey,
  );
  const getCurrentSelectionAnnouncement = useCallback(
    () =>
      selectionEndpoint === "checkIn" && interactionCheckIn
        ? "새 체크인 날짜를 선택하세요."
        : getSelectionAnnouncement(interactionCheckIn, checkOut),
    [checkOut, interactionCheckIn, selectionEndpoint],
  );
  const [selectionAnnouncement, setSelectionAnnouncement] = useState(
    getCurrentSelectionAnnouncement,
  );
  const nextMonth = useMemo(() => addMonths(currentMonth, 1), [currentMonth]);
  const currentMonthWeeks = useMemo(
    () => getCalendarWeeks(currentMonth),
    [currentMonth],
  );
  const nextMonthWeeks = useMemo(
    () => getCalendarWeeks(nextMonth),
    [nextMonth],
  );
  const internalPickerRef = useRef<HTMLDivElement>(null);
  const pickerRef = datePickerRef ?? internalPickerRef;
  const dateCellRefs = useRef(new Map<string, HTMLButtonElement>());
  const dateFocusRequestRef = useRef<"initial" | "roving" | null>(
    variant === "inline" || variant === "sheet" ? null : "initial",
  );
  const pickerId = useId();

  useEffect(() => {
    setSelectionAnnouncement(getCurrentSelectionAnnouncement());
  }, [getCurrentSelectionAnnouncement]);

  useEffect(() => {
    const focusRequest = dateFocusRequestRef.current;
    if (!focusRequest) return;

    dateFocusRequestRef.current = null;
    const dateCell = dateCellRefs.current.get(formatDateKey(focusedDate));
    if (focusRequest === "initial") {
      dateCell?.focus({ preventScroll: true });
    } else {
      dateCell?.focus();
    }
  }, [currentMonth, focusedDate]);

  useEffect(() => {
    if (
      variant !== "inline" ||
      !checkInKey ||
      pickerRef.current?.contains(document.activeElement)
    )
      return;

    // A date saved in the separate mobile calendar must also be visible here.
    dateFocusRequestRef.current = null;
    setCurrentMonth(startOfMonth(initialFocusedDate));
    setFocusedDate(initialFocusedDate);
  }, [checkInKey, initialFocusedDate, pickerRef, variant]);

  const isDateInRange = (date: Date): boolean => {
    if (!checkInKey || !checkOutKey) return false;
    const dateKey = formatDateKey(date);
    return dateKey > checkInKey && dateKey < checkOutKey;
  };

  const isDateSelected = (date: Date): boolean => {
    const dateKey = formatDateKey(date);
    return dateKey === checkInKey || dateKey === checkOutKey;
  };

  const isDateInHoverRange = (date: Date): boolean => {
    if (
      !isSelectingCheckOut ||
      !interactionCheckInKey ||
      checkOutKey ||
      !hoverDateKey ||
      !hoverDate ||
      isDateDisabled(hoverDate)
    ) {
      return false;
    }

    const dateKey = formatDateKey(date);
    return dateKey > interactionCheckInKey && dateKey < hoverDateKey;
  };

  const isPastDate = (date: Date): boolean =>
    formatDateKey(date) < minimumDateKey;

  const isUnavailableDate = (date: Date): boolean =>
    isBlockedStayDateKey(formatDateKey(date));

  const ensureDateIsVisible = useCallback(
    (date: Date) => {
      if (variant === "sheet") return;
      const targetMonthIndex = getMonthIndex(date);
      const currentMonthIndex = getMonthIndex(currentMonth);
      const nextMonthIndex = getMonthIndex(
        isSingleMonth ? currentMonth : nextMonth,
      );

      if (targetMonthIndex < currentMonthIndex) {
        setCurrentMonth(startOfMonth(date));
      } else if (targetMonthIndex > nextMonthIndex) {
        setCurrentMonth(
          isSingleMonth ? startOfMonth(date) : addMonths(date, -1),
        );
      }
    },
    [currentMonth, nextMonth, isSingleMonth, variant],
  );

  const moveDateFocus = useCallback(
    (targetDate: Date, preferredDirection: -1 | 1, requestFocus = true) => {
      const nextFocusedDate = findClosestEnabledDate(
        targetDate,
        preferredDirection,
        isDateDisabled,
      );

      if (!nextFocusedDate) return;

      ensureDateIsVisible(nextFocusedDate);
      dateFocusRequestRef.current = requestFocus ? "roving" : null;
      setFocusedDate(nextFocusedDate);
    },
    [ensureDateIsVisible, isDateDisabled],
  );

  useEffect(() => {
    if (!isDateDisabled(focusedDate)) return;

    moveDateFocus(
      addDays(focusedDate, 1),
      1,
      (variant !== "inline" && variant !== "sheet") ||
        Boolean(pickerRef.current?.contains(document.activeElement)),
    );
  }, [focusedDate, isDateDisabled, moveDateFocus, pickerRef, variant]);

  const selectDate = useCallback(
    (date: Date) => {
      if (isDateDisabled(date)) return;

      dateFocusRequestRef.current = null;
      setFocusedDate(date);

      if (!isSelectingCheckOut || !interactionCheckIn) {
        onDateSelect(date, null);
        setSelectionAnnouncement(
          `${formatKoreanDateLabel(
            date,
          )} 체크인 선택됨. 체크아웃 날짜를 선택하세요.`,
        );
        return;
      }

      onDateSelect(interactionCheckIn, date);
      setSelectionAnnouncement(
        `${formatKoreanDateLabel(
          interactionCheckIn,
        )}부터 ${formatKoreanDateLabel(date)}까지 선택됨`,
      );
    },
    [interactionCheckIn, isDateDisabled, isSelectingCheckOut, onDateSelect],
  );

  const handleDateKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>, date: Date) => {
      let targetDate: Date | null = null;
      let preferredDirection: -1 | 1 = 1;

      switch (event.key) {
        case "ArrowLeft":
          targetDate = addDays(date, -1);
          preferredDirection = -1;
          break;
        case "ArrowRight":
          targetDate = addDays(date, 1);
          break;
        case "ArrowUp":
          targetDate = addDays(date, -DAYS_PER_WEEK);
          preferredDirection = -1;
          break;
        case "ArrowDown":
          targetDate = addDays(date, DAYS_PER_WEEK);
          break;
        case "Home":
          targetDate = addDays(date, -date.getDay());
          break;
        case "End":
          targetDate = addDays(date, DAYS_PER_WEEK - 1 - date.getDay());
          preferredDirection = -1;
          break;
        case "PageUp":
          targetDate = addMonthsPreservingDay(date, -1);
          preferredDirection = -1;
          break;
        case "PageDown":
          targetDate = addMonthsPreservingDay(date, 1);
          break;
        case "Enter":
        case " ":
          event.preventDefault();
          selectDate(date);
          return;
        default:
          return;
      }

      event.preventDefault();
      moveDateFocus(targetDate, preferredDirection);
    },
    [moveDateFocus, selectDate],
  );

  const moveVisibleMonth = (amount: -1 | 1) => {
    const nextCurrentMonth = addMonths(currentMonth, amount);
    const preferredDate = addMonthsPreservingDay(focusedDate, amount);
    const nextFocusedDate = findClosestEnabledDate(
      preferredDate,
      amount,
      isDateDisabled,
    );

    if (!nextFocusedDate) return;

    const nextFocusedMonthIndex = getMonthIndex(nextFocusedDate);
    const nextCurrentMonthIndex = getMonthIndex(nextCurrentMonth);

    if (
      nextFocusedMonthIndex < nextCurrentMonthIndex ||
      nextFocusedMonthIndex > nextCurrentMonthIndex + (isSingleMonth ? 0 : 1)
    ) {
      return;
    }

    setCurrentMonth(nextCurrentMonth);
    dateFocusRequestRef.current = null;
    setFocusedDate(nextFocusedDate);
  };

  const sheetMonths = useMemo(() => {
    const firstMonth = startOfMonth(minimumDate);
    const lastDate = windowEndExclusiveKey
      ? calendarLocalDateToDate(windowEndExclusiveKey)
      : null;
    const count = lastDate
      ? getMonthIndex(lastDate) - getMonthIndex(firstMonth) + 1
      : 12;
    return Array.from({ length: Math.max(1, count) }, (_, index) =>
      addMonths(firstMonth, index),
    );
  }, [minimumDate, windowEndExclusiveKey]);

  const renderCalendar = (
    month: Date,
    calendarWeeks: Array<Array<Date | null>>,
    navigationDirection: -1 | 1,
  ) => {
    const monthName = formatMonthName(month);
    const monthKey = formatDateKey(month);
    const monthHeadingId = `${pickerId}-${monthKey}`;

    const visibleWeeks = hasPairedMonthNavigation
      ? calendarWeeks.filter((week) => week.some(Boolean))
      : calendarWeeks;

    return (
      <div className={styles.calendar}>
        <div className={styles.monthHeader}>
          {(variant === "search" ||
            variant === "inline" ||
            hasPairedMonthNavigation) && (
            <button
              aria-label={
                navigationDirection === -1 ? "이전 달 보기" : "다음 달 보기"
              }
              className={`${styles.monthNavButton} ${
                navigationDirection === -1
                  ? styles.previousMonthButton
                  : styles.nextMonthButton
              }`}
              type="button"
              onClick={() => moveVisibleMonth(navigationDirection)}
            >
              <span aria-hidden="true">
                {navigationDirection === -1 ? "‹" : "›"}
              </span>
            </button>
          )}
          <h3 id={monthHeadingId} className={styles.monthName}>
            {monthName}
          </h3>
          {isSingleMonth && (
            <button
              aria-label="다음 달 보기"
              className={`${styles.monthNavButton} ${styles.nextMonthButton}`}
              type="button"
              onClick={() => moveVisibleMonth(1)}
            >
              <span aria-hidden="true">›</span>
            </button>
          )}
        </div>
        <div
          className={styles.calendarGrid}
          role="grid"
          aria-labelledby={monthHeadingId}
        >
          <div className={styles.weekdays} role="row">
            {WEEKDAYS.map((day) => (
              <div
                key={day.short}
                className={styles.weekday}
                role="columnheader"
                aria-label={day.long}
              >
                {day.short}
              </div>
            ))}
          </div>
          <div className={styles.days} role="rowgroup">
            {visibleWeeks.map((week, weekIndex) => (
              <div
                key={`${monthKey}-week-${weekIndex}`}
                className={styles.week}
                role="row"
              >
                {week.map((date, dayIndex) => {
                  if (!date) {
                    return (
                      <div
                        key={`empty-${weekIndex}-${dayIndex}`}
                        className={`${styles.day} ${styles.empty}`}
                        role="gridcell"
                        aria-hidden="true"
                      />
                    );
                  }

                  const dateKey = formatDateKey(date);
                  const isSelected = isDateSelected(date);
                  const isInRange = isDateInRange(date);
                  const isInHoverRange = isDateInHoverRange(date);
                  const isDisabled = isDateDisabled(date);
                  const isPast = isPastDate(date);
                  const isUnavailable = isDisabled && isUnavailableDate(date);
                  const isStart = dateKey === checkInKey;
                  const isEnd = dateKey === checkOutKey;
                  const isRangeStart =
                    hasVisibleRange && dateKey === interactionCheckInKey;
                  const isRangeEnd =
                    hasVisibleRange && dateKey === visibleRangeEndKey;

                  return (
                    <button
                      key={dateKey}
                      ref={(element) => {
                        if (element) {
                          dateCellRefs.current.set(dateKey, element);
                        } else {
                          dateCellRefs.current.delete(dateKey);
                        }
                      }}
                      type="button"
                      role="gridcell"
                      aria-label={formatKoreanDateLabel(date)}
                      aria-selected={isSelected}
                      aria-disabled={isDisabled}
                      disabled={isDisabled}
                      tabIndex={
                        !isDisabled && focusedDateKey === dateKey ? 0 : -1
                      }
                      className={`${styles.day} ${
                        isSelected ? styles.selected : ""
                      } ${isInRange || isInHoverRange ? styles.inRange : ""} ${
                        isDisabled ? styles.disabled : ""
                      } ${isPast ? styles.past : ""} ${
                        isUnavailable ? styles.unavailable : ""
                      } ${isStart ? styles.start : ""} ${
                        isEnd ? styles.end : ""
                      } ${isRangeStart ? styles.rangeStart : ""} ${
                        isRangeEnd ? styles.rangeEnd : ""
                      }`}
                      onClick={(event) => {
                        event.stopPropagation();
                        selectDate(date);
                      }}
                      onKeyDown={(event) => handleDateKeyDown(event, date)}
                      onMouseEnter={() => {
                        if (
                          isSelectingCheckOut &&
                          interactionCheckIn &&
                          !isDisabled
                        ) {
                          setHoverDate(date);
                        }
                      }}
                      onMouseLeave={() => setHoverDate(null)}
                    >
                      <span className={styles.dayNumber}>{date.getDate()}</span>
                      {(isPast || isUnavailable) && (
                        <span className={styles.dayStrike}>−</span>
                      )}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const handlePickerKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (isKeyboardHelpOpen) return;
    if (isBooking && event.key === "?") {
      event.preventDefault();
      setIsKeyboardHelpOpen(true);
      return;
    }
    if (event.key !== "Escape" || variant === "inline") return;

    event.preventDefault();
    event.stopPropagation();
    (onEscape ?? onClose)();
  };

  return (
    <div
      aria-label="날짜 선택"
      className={`${styles.datePicker} ${
        variant === "compact" ? styles.compact : ""
      } ${variant === "search" || variant === "inline" || variant === "sheet" || hasPairedMonthNavigation ? styles.search : ""} ${variant === "inline" || variant === "sheet" ? styles.inline : ""} ${variant === "sheet" ? styles.sheet : ""} ${isBooking ? styles.booking : ""} ${variant === "review" ? styles.review : ""}`}
      ref={pickerRef}
      onKeyDownCapture={handlePickerKeyDown}
      role="group"
    >
      {(variant === "default" || variant === "compact") && (
        <div className={styles.navHeader}>
          <button
            aria-label="이전 달 보기"
            className={styles.monthNavButton}
            type="button"
            onClick={() => moveVisibleMonth(-1)}
          >
            ←
          </button>
          <span className={styles.navTitle}>
            {formatMonthName(currentMonth)}
          </span>
          <button
            aria-label="다음 달 보기"
            className={styles.monthNavButton}
            type="button"
            onClick={() => moveVisibleMonth(1)}
          >
            →
          </button>
        </div>
      )}

      <div className={styles.calendarsScrollArea}>
        <div className={styles.calendars}>
          {variant === "sheet" ? (
            sheetMonths.map((month) => (
              <div
                className={styles.calendarWrapper}
                key={formatDateKey(month)}
              >
                {renderCalendar(month, getCalendarWeeks(month), -1)}
              </div>
            ))
          ) : (
            <>
              <div className={styles.calendarWrapper}>
                {renderCalendar(currentMonth, currentMonthWeeks, -1)}
              </div>
              {!isSingleMonth && (
                <div className={styles.calendarWrapper}>
                  {renderCalendar(nextMonth, nextMonthWeeks, 1)}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <div
        className={styles.liveRegion}
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        {selectionAnnouncement}
      </div>

      {!hideFooter && (
        <div className={styles.footer}>
          {isBooking && (
            <button
              type="button"
              className={styles.keyboardButton}
              aria-label="키보드 단축키 보기"
              aria-haspopup="dialog"
              onClick={() => setIsKeyboardHelpOpen(true)}
            >
              <svg
                width="22"
                height="18"
                viewBox="0 0 24 20"
                fill="none"
                aria-hidden="true"
              >
                <rect
                  x="1"
                  y="2"
                  width="22"
                  height="16"
                  rx="2"
                  stroke="currentColor"
                  strokeWidth="1.5"
                />
                <path
                  d="M4 6h2m2 0h2m2 0h2m2 0h2m2 0h1M4 9h2m2 0h2m2 0h2m2 0h2m2 0h1M7 14h10"
                  stroke="currentColor"
                  strokeWidth="1.5"
                />
              </svg>
            </button>
          )}
          <button
            className={styles.clearButton}
            type="button"
            onClick={() => {
              onDateSelect(null, null);
              setSelectionAnnouncement("선택한 날짜가 지워졌습니다.");
            }}
          >
            날짜 지우기
          </button>
          {variant !== "inline" && (
            <button
              className={styles.closeButton}
              type="button"
              onClick={onClose}
            >
              닫기
            </button>
          )}
        </div>
      )}
      {isBooking && (
        <Dialog
          isOpen={isKeyboardHelpOpen}
          onClose={() => setIsKeyboardHelpOpen(false)}
          title="키보드 단축키"
          showHeader={false}
          size="sm"
        >
          <div className={styles.keyboardHelp}>
            <h2>키보드 단축키</h2>
            <dl>
              <div>
                <dt>Enter / Space</dt>
                <dd>해당 날짜 선택</dd>
              </div>
              <div>
                <dt>← / →</dt>
                <dd>하루 전 / 다음 날로 이동</dd>
              </div>
              <div>
                <dt>↑ / ↓</dt>
                <dd>이전 주 / 다음 주로 이동</dd>
              </div>
              <div>
                <dt>Page Up / Down</dt>
                <dd>이전 달 / 다음 달로 이동</dd>
              </div>
              <div>
                <dt>Home / End</dt>
                <dd>해당 주의 첫날 / 마지막 날로 이동</dd>
              </div>
              <div>
                <dt>Esc</dt>
                <dd>달력 닫기</dd>
              </div>
            </dl>
            <button
              type="button"
              className={styles.clearButton}
              onClick={() => setIsKeyboardHelpOpen(false)}
            >
              달력으로 돌아가기
            </button>
          </div>
        </Dialog>
      )}
    </div>
  );
};
